// Creator-facing Tips module — lists every tip a creator has received, split
// across Pending (pre-Connect held credits) / Applied (already netted off a
// platform invoice) / Paid via Connect (direct payouts) / Top fans. Data comes
// from GET /api/tips/me → {tips, total_pending_cents, total_applied_cents,
// total_payout_cents}. Refreshes once on mount; silent fail → empty table.
import { useEffect, useMemo, useState } from "react";
import { Heart, Clock, CheckCircle, TrendUp, CurrencyDollar, Users, PaperPlaneTilt, CheckSquare } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import ThankYouModal from "@/components/ThankYouModal";

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

const STATUS_BADGE = {
  held: { label: "Pending credit", color: "bg-[#FFF3CD] text-[#8A5A00]" },
  applying: { label: "Pending credit", color: "bg-[#FFF3CD] text-[#8A5A00]" },
  applied: { label: "Applied to invoice", color: "bg-[#D4EDDA] text-[#2F6B3A]" },
  paid_via_connect: { label: "Paid via Stripe", color: "bg-[#D1ECF1] text-[#0C5460]" },
};

export default function TipsModule() {
  const [data, setData] = useState(null);
  const [subTab, setSubTab] = useState("pending");
  const [thankFan, setThankFan] = useState(null);  // {email, count, total, latest_at}

  const load = async () => {
    try {
      const r = await api.get("/tips/me");
      setData(r.data);
    } catch {
      setData({ tips: [], total_pending_cents: 0, total_applied_cents: 0, total_payout_cents: 0, last_thank_you_by_email: {} });
    }
  };
  useEffect(() => { load(); }, []);

  const { pending, applied, paid, topFans } = useMemo(() => {
    const tips = data?.tips || [];
    const topMap = {};
    for (const t of tips) {
      const email = t.from_email || "Anonymous";
      topMap[email] = topMap[email] || { email, count: 0, total: 0, latest_at: null };
      topMap[email].count += 1;
      topMap[email].total += Number(t.amount_cents || 0);
      if (!topMap[email].latest_at || (t.created_at || "") > topMap[email].latest_at) {
        topMap[email].latest_at = t.created_at;
      }
    }
    return {
      pending: tips.filter((t) => t.status === "held" || t.status === "applying"),
      applied: tips.filter((t) => t.status === "applied"),
      paid: tips.filter((t) => t.status === "paid_via_connect"),
      topFans: Object.values(topMap).sort((a, b) => b.total - a.total).slice(0, 20),
    };
  }, [data]);

  if (!data) return <div className="text-sm text-[#6B6A68]">Loading tips…</div>;

  const totalTipped = (data.total_pending_cents + data.total_applied_cents + data.total_payout_cents) || 0;
  const totalTips = (data.tips || []).length;

  const subTabs = [
    { id: "pending", label: "Pending", icon: Clock, count: pending.length, hint: "Tips received before you activated Stripe Connect. These are held as credits and auto-applied to your next platform subscription invoice." },
    { id: "applied", label: "Applied", icon: CheckCircle, count: applied.length, hint: "Tip credits that have already reduced a past platform invoice." },
    { id: "paid", label: "Paid via Stripe", icon: TrendUp, count: paid.length, hint: "Tips that went as direct payouts to your Stripe Connect account. Supporters pay the My Silo service fee on top, so the full tip amount reaches you." },
    { id: "top", label: "Top fans", icon: Users, count: topFans.length, hint: "Leaderboard of fans who tipped you most — use this to say thanks personally." },
  ];

  const rows = subTab === "pending" ? pending : subTab === "applied" ? applied : subTab === "paid" ? paid : null;

  return (
    <div data-testid="tips-module">
      {/* Header + summary */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
            <Heart size={22} weight="duotone" className="text-[#C16E5A]" />
            Tips from your fans
          </h2>
          <p className="text-sm text-[#6B6A68] mt-1 max-w-xl">
            Every tip ever left on your silos. Fans who tip before you've activated Stripe Connect leave credits here that automatically net off your next platform invoice.
          </p>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="tips-stats">
        <StatCard label="Total tips" value={totalTips} icon={Heart} />
        <StatCard label="Lifetime $" value={fmt$(totalTipped)} icon={CurrencyDollar} />
        <StatCard label="Pending" value={fmt$(data.total_pending_cents)} icon={Clock} tone="warn"
          infoTip="Waiting to credit your next platform invoice. Activate Stripe Connect to switch future tips to direct payouts." />
        <StatCard label="Paid out" value={fmt$(data.total_payout_cents)} icon={TrendUp} tone="success" />
      </div>

      {/* Sub-tabs — div containers (not <button>) so nested InfoTip button doesn't trip hydration.
          Feb 21, 2026 (late eve) — mobile fix: the 4 labels were squishing
          and "Paid via Stripe" wrapped to 3 lines on a phone. Wrapped the
          row in an overflow-x-auto container + whitespace-nowrap on each
          tab so the row scrolls horizontally instead of squishing. */}
      <div className="overflow-x-auto -mx-1 mb-5 scrollbar-hide">
        <div className="flex items-center gap-1 border-b hairline px-1 min-w-max" data-testid="tips-subtabs" role="tablist">
          {subTabs.map((t) => (
            <div key={t.id} role="tab" aria-selected={subTab === t.id}
              className={`px-4 py-2.5 text-sm flex items-center gap-1.5 border-b-2 -mb-[1px] transition-colors cursor-pointer whitespace-nowrap shrink-0 ${subTab === t.id ? "border-[#C16E5A] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <button onClick={() => setSubTab(t.id)} data-testid={`tips-subtab-${t.id}`}
                className="flex items-center gap-1.5 focus:outline-none whitespace-nowrap">
                <t.icon size={14} weight="duotone" /> {t.label}
                <span className="text-[10px] text-[#6B6A68] tabular-nums">({t.count})</span>
              </button>
              <InfoTip placement="top" width={260}>{t.hint}</InfoTip>
            </div>
          ))}
        </div>
      </div>

      {/* Body */}
      {subTab === "top" ? (
        <TopFansTable
          rows={topFans}
          lastThanked={data.last_thank_you_by_email || {}}
          onThank={(fan) => setThankFan(fan)}
        />
      ) : (
        <TipsTable rows={rows} emptyMessage={emptyCopy(subTab)} lastThanked={data.last_thank_you_by_email || {}} />
      )}

      <ThankYouModal
        open={!!thankFan}
        fan={thankFan}
        onClose={() => setThankFan(null)}
        onSent={(email, sentAt) => {
          // Optimistically mark this fan as recently thanked so the button
          // re-renders disabled without waiting for a full /tips/me refresh.
          setData((d) => d ? { ...d, last_thank_you_by_email: { ...(d.last_thank_you_by_email || {}), [email.toLowerCase()]: sentAt } } : d);
        }}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, infoTip }) {
  const toneBg = tone === "warn" ? "bg-[#FFF9E6]" : tone === "success" ? "bg-[#F0F7F2]" : "bg-white";
  return (
    <div className={`border hairline rounded-lg p-4 ${toneBg}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
        {Icon && <Icon size={12} weight="duotone" />} {label}
        {infoTip && <InfoTip placement="top" width={240}>{infoTip}</InfoTip>}
      </div>
      <div className="font-serif-display text-2xl mt-1 text-[#1C1B1A] tabular-nums">{value}</div>
    </div>
  );
}

function TipsTable({ rows, emptyMessage, lastThanked = {} }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white border hairline rounded-lg p-8 text-center text-sm text-[#6B6A68]" data-testid="tips-empty">
        {emptyMessage}
      </div>
    );
  }
  const now = Date.now();
  const isRecent = (iso) => {
    if (!iso) return false;
    try { return (now - new Date(iso).getTime()) < 24 * 60 * 60 * 1000; }
    catch { return false; }
  };
  return (
    <div className="bg-white border hairline rounded-lg overflow-x-auto" data-testid="tips-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-[#F0F1EE] text-[#6B6A68] text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-3">When</th>
            <th className="text-left px-4 py-3">Silo</th>
            <th className="text-left px-4 py-3">From</th>
            <th className="text-left px-4 py-3">Note</th>
            <th className="text-right px-4 py-3">Amount</th>
            <th className="text-left px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const badge = STATUS_BADGE[t.status] || { label: t.status, color: "bg-[#F0F1EE] text-[#6B6A68]" };
            const fanEmail = (t.from_email || "").toLowerCase();
            const thanked = !!fanEmail && isRecent(lastThanked[fanEmail]);
            return (
              <tr key={t.id} className="border-t hairline" data-testid={`tip-row-${t.id}`}>
                <td className="px-4 py-3 text-[#1C1B1A] whitespace-nowrap">{fmtDate(t.created_at)}</td>
                <td className="px-4 py-3 text-[#6B6A68] font-mono text-xs">/s/{t.silo_slug || "—"}</td>
                <td className="px-4 py-3 text-[#6B6A68]">
                  <div className="flex items-center gap-1.5">
                    <span>{t.from_email || "Anonymous"}</span>
                    {thanked && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#D4EDDA] text-[#2F6B3A] inline-flex items-center gap-0.5"
                        title="Thank-you email sent within the last 24h"
                        data-testid={`tip-thanked-${t.id}`}>
                        <CheckSquare size={9} weight="duotone" /> Thanked
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-[#6B6A68] italic max-w-xs truncate" title={t.note || ""}>
                  {t.note ? `“${t.note}”` : "—"}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-[#1C1B1A] font-medium">{fmt$(t.amount_cents)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-2 py-1 rounded ${badge.color}`}>{badge.label}</span>
                  {t.applied_at && <span className="text-[10px] text-[#6B6A68] ml-1.5">on {fmtDate(t.applied_at)}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TopFansTable({ rows, lastThanked = {}, onThank }) {
  if (rows.length === 0) {
    return (
      <div className="bg-white border hairline rounded-lg p-8 text-center text-sm text-[#6B6A68]" data-testid="tips-top-empty">
        No tips yet — once fans start tipping you, the most generous will show up here.
      </div>
    );
  }
  const now = Date.now();
  const isRecent = (iso) => {
    if (!iso) return false;
    try { return (now - new Date(iso).getTime()) < 24 * 60 * 60 * 1000; }
    catch { return false; }
  };
  return (
    <div className="bg-white border hairline rounded-lg overflow-x-auto" data-testid="tips-top-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-[#F0F1EE] text-[#6B6A68] text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-3 w-12">#</th>
            <th className="text-left px-4 py-3">Fan</th>
            <th className="text-right px-4 py-3">Tips</th>
            <th className="text-right px-4 py-3">Lifetime $</th>
            <th className="text-left px-4 py-3">Last seen</th>
            <th className="text-right px-4 py-3 w-40">Thank you</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f, idx) => {
            const isAnon = f.email === "Anonymous";
            const cooldown = isRecent(lastThanked[f.email.toLowerCase()]);
            return (
              <tr key={f.email} className="border-t hairline" data-testid={`top-fan-${idx}`}>
                <td className="px-4 py-3 text-[#6B6A68] tabular-nums">{idx + 1}</td>
                <td className="px-4 py-3 text-[#1C1B1A]">{isAnon ? <span className="italic text-[#6B6A68]">Anonymous</span> : f.email}</td>
                <td className="px-4 py-3 text-right tabular-nums">{f.count}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#1C1B1A] font-medium">{fmt$(f.total)}</td>
                <td className="px-4 py-3 text-[#6B6A68]">{fmtDate(f.latest_at)}</td>
                <td className="px-4 py-3 text-right">
                  {isAnon ? (
                    <span className="text-[10px] text-[#6B6A68] italic">No email</span>
                  ) : cooldown ? (
                    <span className="text-[10px] text-[#2F6B3A] inline-flex items-center gap-1" data-testid={`top-fan-thanked-${idx}`}>
                      <CheckSquare size={12} weight="duotone" /> Thanked recently
                    </span>
                  ) : (
                    <button onClick={() => onThank?.(f)} data-testid={`top-fan-thank-${idx}`}
                      className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-1.5">
                      <PaperPlaneTilt size={12} weight="duotone" /> Send thanks
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function emptyCopy(sub) {
  if (sub === "pending") return "No pending tips. When a fan tips you before you've activated Stripe Connect, the amount appears here and credits your next invoice.";
  if (sub === "applied") return "No applied credits yet. Once a pending tip nets off a platform invoice it moves here.";
  if (sub === "paid") return "No direct payouts yet. After you activate Stripe Connect, fan tips go straight to your account and log here.";
  return "No tips yet.";
}
